import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminProductAPI, adminCategoryAPI } from '../services/api';
import '../styles/ProductForm.css';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showSeoSection, setShowSeoSection] = useState(false);
  const [formData, setFormData] = useState({
    catalogNumber: '',
    productName: '',
    description: '',
    packSize: '',
    productImage: '',
    productImage2: '',
    productCategory: '',
    brandName: '',
    applications: '',
    productInsert: '',
    storage: '',
    source: '',
    dilutions: '',
    clonality: '',
    isotype: '',
    concentration: '',
    immunogen: '',
    reactivity: '',
    sensitivity: '',
    standard: '',
    detectionRange: '',
    sampleType: '',
    assayLength: '',
    alternativeNames: '',
    testPrinciple: '',
    researchArea: '',
    assayType: '',
    seoTitle: '',
    seoDescription: '',
    seoImage: '',
    status: 'Active'
  });

  const fetchCategoriesAndBrands = React.useCallback(async () => {
    try {
      const categoriesRes = await adminCategoryAPI.getAll({ limit: 100, status: 'Active' });

      setCategories(
        Array.isArray(categoriesRes.data.data.categories)
          ? categoriesRes.data.data.categories
          : []
      );
    } catch (err) {
      console.error('Error fetching categories and brands:', err);
    }
  }, []);

  const fetchProduct = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminProductAPI.getById(id);
      console.log("response.data", response.data);
      setFormData(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.response?.data?.message || 'Failed to fetch product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCategoriesAndBrands();
    if (isEditMode) {
      fetchProduct();
    }
  }, [fetchCategoriesAndBrands, fetchProduct, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await adminProductAPI.update(id, formData);
      } else {
        await adminProductAPI.create(formData);
      }
      navigate('/products');
    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="product-form-container">
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  return (
    <div className="product-form-container">
      <div className="product-form-header">
        <h1>{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/products')}>
          Back to Products
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="product-form">
        {/* Basic Information */}
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="catalogNumber">Catalogue Number *</label>
              <input
                type="text"
                id="catalogNumber"
                name="catalogNumber"
                value={formData.catalogNumber}
                onChange={handleChange}
                required
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="productName">Product Name *</label>
              <input
                type="text"
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="productCategory">Product Category *</label>
              <select
                id="productCategory"
                name="productCategory"
                value={formData.productCategory}
                onChange={handleChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category.title}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="brandName">Brand Name</label>
              <input
                type="text"
                id="brandName"
                name="brandName"
                value={formData.brandName}
                onChange={handleChange}
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              maxLength={5000}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="packSize">Pack Size</label>
              <input
                type="text"
                id="packSize"
                name="packSize"
                value={formData.packSize}
                onChange={handleChange}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h2>Product Images</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="productImage">Product Image URL</label>
              <input
                type="text"
                id="productImage"
                name="productImage"
                value={formData.productImage}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg or Google Drive link"
              />
              <small>Enter image URL </small>
            </div>
            <div className="form-group">
              <label htmlFor="productImage2">Product Image 2 URL</label>
              <input
                type="text"
                id="productImage2"
                name="productImage2"
                value={formData.productImage2}
                onChange={handleChange}
                placeholder="https://example.com/image2.jpg or Google Drive link"
              />
              <small>Enter image URL </small>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="form-section">
          <h2>Technical Details</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="clonality">Clonality</label>
              <select
                id="clonality"
                name="clonality"
                value={formData.clonality}
                onChange={handleChange}
              >
                <option value="">Select Clonality</option>
                <option value="Monoclonal">Monoclonal</option>
                <option value="Polyclonal">Polyclonal</option>
                <option value="Oligoclonal">Oligoclonal</option>
                <option value="Recombinant">Recombinant</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="isotype">Isotype</label>
              <input
                type="text"
                id="isotype"
                name="isotype"
                value={formData.isotype}
                onChange={handleChange}
                maxLength={300}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="source">Source</label>
              <input
                type="text"
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="concentration">Concentration</label>
              <input
                type="text"
                id="concentration"
                name="concentration"
                value={formData.concentration}
                onChange={handleChange}
                maxLength={100}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dilutions">Dilutions</label>
              <input
                type="text"
                id="dilutions"
                name="dilutions"
                value={formData.dilutions}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="reactivity">Reactivity</label>
              <input
                type="text"
                id="reactivity"
                name="reactivity"
                value={formData.reactivity}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="immunogen">Immunogen</label>
            <textarea
              id="immunogen"
              name="immunogen"
              value={formData.immunogen}
              onChange={handleChange}
              rows={3}
              maxLength={1000}
            />
          </div>
        </div>

        {/* Applications & Usage */}
        <div className="form-section">
          <h2>Applications & Usage</h2>
          <div className="form-group">
            <label htmlFor="applications">Applications</label>
            <textarea
              id="applications"
              name="applications"
              value={formData.applications}
              onChange={handleChange}
              rows={3}
              maxLength={2000}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="storage">Storage</label>
              <input
                type="text"
                id="storage"
                name="storage"
                value={formData.storage}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="productInsert">Product Insert/Manual URL</label>
              <input
                type="text"
                id="productInsert"
                name="productInsert"
                value={formData.productInsert}
                onChange={handleChange}
                maxLength={5000}
                placeholder="https://example.com/manual.pdf"
              />
            </div>
          </div>
        </div>

        {/* Assay Information */}
        <div className="form-section">
          <h2>Assay Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="assayType">Assay Type</label>
              <input
                type="text"
                id="assayType"
                name="assayType"
                value={formData.assayType}
                onChange={handleChange}
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label htmlFor="assayLength">Assay Length</label>
              <input
                type="text"
                id="assayLength"
                name="assayLength"
                value={formData.assayLength}
                onChange={handleChange}
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sensitivity">Sensitivity</label>
              <input
                type="text"
                id="sensitivity"
                name="sensitivity"
                value={formData.sensitivity}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="detectionRange">Detection Range</label>
              <input
                type="text"
                id="detectionRange"
                name="detectionRange"
                value={formData.detectionRange}
                onChange={handleChange}
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="standard">Standard</label>
              <input
                type="text"
                id="standard"
                name="standard"
                value={formData.standard}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="sampleType">Sample Type</label>
              <input
                type="text"
                id="sampleType"
                name="sampleType"
                value={formData.sampleType}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="testPrinciple">Test Principle</label>
            <textarea
              id="testPrinciple"
              name="testPrinciple"
              value={formData.testPrinciple}
              onChange={handleChange}
              rows={3}
              maxLength={1000}
            />
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h2>Additional Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="researchArea">Research Area</label>
              <input
                type="text"
                id="researchArea"
                name="researchArea"
                value={formData.researchArea}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <div className="form-group">
              <label htmlFor="alternativeNames">Alternative Names</label>
              <input
                type="text"
                id="alternativeNames"
                name="alternativeNames"
                value={formData.alternativeNames}
                onChange={handleChange}
                maxLength={1000}
              />
            </div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="form-section pf-seo-section">
          <button
            type="button"
            className="pf-seo-toggle-header"
            onClick={() => setShowSeoSection(prev => !prev)}
          >
            <span className="pf-seo-toggle-label">
              SEO Settings
              <small className="pf-seo-toggle-hint">(optional — overrides auto-generated SEO)</small>
            </span>
            <span className={`pf-seo-toggle-icon ${showSeoSection ? 'open' : ''}`}>&#8250;</span>
          </button>

          {showSeoSection && (
            <div className="pf-seo-body">
              {/* Google Snippet Preview */}
              <div className="pf-seo-preview">
                <p className="pf-seo-preview-label">Google search preview</p>
                <div className="pf-seo-snippet">
                  <p className="pf-snippet-title">
                    {formData.seoTitle || formData.productName || 'Product Title | EC Biolabs'}
                  </p>
                  <p className="pf-snippet-url">ecbiolabs.com/products/{formData.catalogNumber || '…'}</p>
                  <p className="pf-snippet-desc">
                    {formData.seoDescription || formData.description || 'No meta description set.'}
                  </p>
                </div>
              </div>

              {/* Meta Title */}
              <div className="pf-seo-field">
                <div className="pf-seo-field-header">
                  <label htmlFor="seoTitle">SEO Title</label>
                  <span className={`pf-seo-count ${(formData.seoTitle || '').length > 60 ? 'warn' : ''}`}>
                    {(formData.seoTitle || '').length}/70
                  </span>
                </div>
                <input
                  type="text"
                  id="seoTitle"
                  name="seoTitle"
                  value={formData.seoTitle || ''}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Leave blank to auto-generate from product name"
                />
                <small>Recommended: 50–60 characters</small>
              </div>

              {/* Meta Description */}
              <div className="pf-seo-field">
                <div className="pf-seo-field-header">
                  <label htmlFor="seoDescription">SEO Description</label>
                  <span className={`pf-seo-count ${(formData.seoDescription || '').length > 180 ? 'warn' : ''}`}>
                    {(formData.seoDescription || '').length}/200
                  </span>
                </div>
                <textarea
                  id="seoDescription"
                  name="seoDescription"
                  value={formData.seoDescription || ''}
                  onChange={handleChange}
                  rows={3}
                  maxLength={200}
                  placeholder="Leave blank to auto-generate from product description"
                />
                <small>Recommended: 120–160 characters</small>
              </div>

              {/* OG Image */}
              <div className="pf-seo-field">
                <div className="pf-seo-field-header">
                  <label htmlFor="seoImage">Open Graph Image URL</label>
                  <small className="pf-seo-field-hint">for social sharing</small>
                </div>
                <input
                  type="text"
                  id="seoImage"
                  name="seoImage"
                  value={formData.seoImage || ''}
                  onChange={handleChange}
                  placeholder="Leave blank to use product image (recommended: 1200×630px)"
                />
                {formData.seoImage && (
                  <div className="pf-seo-og-preview">
                    <img
                      src={formData.seoImage}
                      alt="OG preview"
                      onError={e => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/products')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Create Product')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
